import * as React from 'react';
import { ArrowBack, ArrowDropDown, EmailOutlined, Link } from "@mui/icons-material";
import { IconButton, List, Snackbar } from "@mui/material";
import LabelledIconButton from "modules/common/components/buttons/labelledIconButton";
import { generateMailTo, getDefaultEmailBody, getDefaultEmailSubject } from 'utils/shareHelpers';
import Callout from 'modules/common/components/callout';
import MoreOptionsItem from 'modules/common/components/moreOptionsItem';
import { GlobalApplicationState } from 'globalApplicationState';
import { ConnectedProps, connect } from 'react-redux';
import { ContentType, IShareLinkv1, LINK_COPIED_SNACKBAR_TEXT, LOADING_SNACKBAR_TEXT, ShareContentMapType } from './models';
import { sharingApi } from 'api/instances';

import './sharingPages.sass'


interface ComponentProps {
    onCloseDialog: () => void;
    tenantName: string;
    contentTitle: string;
    contentId: string;
    contentStyling: any;
    contentType: ContentType;
    isPublic: boolean;
}

enum ChannelSelection {
    None,
    Portal,
    SharePoint,
    Teams
}

const InternalShareScreen: React.FunctionComponent<PropsWithRedux> = (props) => {
    //If we get a bad setup, just default to portal.
    const getInitialChannelPreference = () => {
        const linkPreference = props.linkPreference ? parseInt(props.linkPreference) : ChannelSelection.Portal;

        if (linkPreference === ChannelSelection.Teams && props.teamsShareEnabled) {
            return ChannelSelection.Teams;
        }

        else if (linkPreference === ChannelSelection.SharePoint && props.sharepointShareEnabled) {
            return ChannelSelection.SharePoint;
        }

        else {
            return ChannelSelection.Portal;
        }
    }

    const getShareLinkAndUpdateSnackbar = async (linkDetails: IShareLinkv1, copyFinishedText: string) => {
        setSnackbarAndText(LOADING_SNACKBAR_TEXT, true);

        const link = await sharingApi.getShareLink(linkDetails);
        navigator.clipboard.writeText(link);
        setSnackbarAndText(copyFinishedText, true);
    }

    const copyLinkRef = React.useRef(null);
    const [calloutOpen, setCalloutOpen] = React.useState(false);

    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");

    const [selectedPlatform, setSelectedPlatform] = React.useState(getInitialChannelPreference());

    const portalLabel = props.portalShareLabel.trim() === "" ? "Portal" : props.portalShareLabel;
    const sharePointLabel = props.sharePointShareLabel.trim() === "" ? "MS Sharepoint" : props.sharePointShareLabel;
    const teamsLabel = props.teamsShareLabel.trim() === "" ? "MS Teams" : props.teamsShareLabel;

    const allChannelsDisabled = !(props.sharepointShareEnabled || props.teamsShareEnabled || props.portalShareEnabled);

    const onEmailClicked = async () => {
        const subject = getDefaultEmailSubject(props.contentType, props.tenantName, props.contentTitle);
        const body = getDefaultEmailBody(props.contentType, props.tenantName);
        const linkDetails: IShareLinkv1 = { 
            contentType: fetchShareTypeEnum(props.contentType, getInitialChannelPreference()), 
            contentId: props.contentId 
        };

        setSnackbarAndText(LOADING_SNACKBAR_TEXT, true);

        const link = await sharingApi.getShareLink(linkDetails);
        const linkToOpen = generateMailTo(subject, body, link);
        window.open(linkToOpen);
    }

    const onLinkClicked = async () => {
        const linkDetails: IShareLinkv1 = { 
            contentType: fetchShareTypeEnum(props.contentType, selectedPlatform), 
            contentId: props.contentId 
        };

        await getShareLinkAndUpdateSnackbar(linkDetails, LINK_COPIED_SNACKBAR_TEXT);
    }

    const getLinkLabel = () => {
        return <div className="link-label-container" onClick={() => setCalloutOpen(true)}>
            <span className="link-label">Copy link</span>
            <ArrowDropDown className="link-label-icon" style={{rotate: calloutOpen ? "180deg" : ""}}/>
        </div>;
    }

    const onChannelSelected = async(platformName: string, platform: ChannelSelection) => {
        setSelectedPlatform(platform);
        setCalloutOpen(false);

        const linkDetails: IShareLinkv1 = {
            contentType: fetchShareTypeEnum(props.contentType, platform), 
            contentId: props.contentId
        };

        await getShareLinkAndUpdateSnackbar(linkDetails, `Link copied and will open in ${platformName}`);
    }

    const fetchShareTypeEnum = (contentType: ContentType, targetPlatform: ChannelSelection): ShareContentMapType => {
        if (contentType === ContentType.Post) {
            switch(targetPlatform) {
                case ChannelSelection.Teams:
                    return ShareContentMapType.TeamsPost;

                case ChannelSelection.SharePoint:
                    return ShareContentMapType.SharePointPost;
                    
                case ChannelSelection.Portal:
                default:
                    return ShareContentMapType.PortalPost;
            }
        }

        //Event
        else {
            switch(targetPlatform) {
                case ChannelSelection.Teams:
                    return ShareContentMapType.TeamsEvent;

                case ChannelSelection.SharePoint:
                    return ShareContentMapType.SharePointEvent;

                case ChannelSelection.Portal:
                default:
                    return ShareContentMapType.PortalEvent;
            }
        }
    }

    const setSnackbarAndText = (text: string, open: boolean) => {
        setSnackbarText(text);
        setSnackbarOpen(open);
    }

    return (
        <div className="share-screen">
            <div className="contents">

                <IconButton className="back-button" onClick={props.onCloseDialog}>
                    <ArrowBack className="icon-styling"/>
                </IconButton>

                <div style={{...props.contentStyling}} className="body">
                    <span className="share-label"> Share with people in {props.tenantName}</span>
                    <div className="share-options-container"> 
                        <LabelledIconButton 
                            label={"Email"}
                            icon={ <EmailOutlined className="icon-styling" /> }
                            onClick={onEmailClicked}
                        />

                        <div ref={copyLinkRef}>
                            <LabelledIconButton 
                                label={getLinkLabel()}
                                icon={ <Link className="icon-styling" /> }
                                onClick={onLinkClicked}
                            />
                            <Callout
                                anchorEl={copyLinkRef.current}
                                open={calloutOpen}
                                setOpen={setCalloutOpen}
                                popoverProps={{
                                    style: { padding: 0 }
                                }}
                                arrowPosition='none'
                                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                transformOrigin={{vertical: "top", horizontal: "left"}}
                                contentClassName='share-callout-content'
                            >
                                <div className="callout-container">
                                    <span className="copy-link-text">Copy link that will open in...</span>
                                    <List disablePadding>
                                        {(allChannelsDisabled || props.portalShareEnabled) &&
                                            <MoreOptionsItem
                                                key={ChannelSelection.Portal}
                                                text={portalLabel}
                                                textStyle={{fontSize: "14px", fontWeight: selectedPlatform === ChannelSelection.Portal ? 500 : {}}}
                                                buttonStyle={selectedPlatform === ChannelSelection.Portal ? {backgroundColor: "#F2F2EF"} : {}}
                                                onClick={async() => {
                                                    await onChannelSelected(portalLabel, ChannelSelection.Portal);
                                                }}
                                            />
                                        }

                                        {props.sharepointShareEnabled &&
                                            <MoreOptionsItem
                                                key={ChannelSelection.SharePoint}
                                                text={sharePointLabel}
                                                textStyle={{fontSize: "14px", fontWeight: selectedPlatform === ChannelSelection.SharePoint ? 500 : {}}}
                                                buttonStyle={selectedPlatform === ChannelSelection.SharePoint ? {backgroundColor: "#F2F2EF"} : {}}
                                                onClick={async() => {
                                                    await onChannelSelected(sharePointLabel, ChannelSelection.SharePoint);
                                                }}
                                            />
                                        }

                                        {props.teamsShareEnabled &&
                                            <MoreOptionsItem
                                                key={ChannelSelection.Teams}
                                                text={teamsLabel}
                                                textStyle={{fontSize: "14px", fontWeight: selectedPlatform === ChannelSelection.Teams ? 500 : {}}}
                                                buttonStyle={selectedPlatform === ChannelSelection.Teams ? {backgroundColor: "#F2F2EF"} : {}}
                                                onClick={async() => {
                                                    await onChannelSelected(teamsLabel, ChannelSelection.Teams);
                                                }}
                                            />
                                        }
                                    </List>
                                </div>    
                            </Callout>
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={null}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
                ContentProps={{
                    style: { minWidth: "50px", backgroundColor: "#36404A" }
                }}
                message={snackbarText}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            />
        </div>
    );

}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        tenantId: state.tenant.id,
        sharePointShareLabel: state.settings.clientSettings.shareUrlsConfig.spUrlShareLinkName,
        portalShareLabel: state.settings.clientSettings.shareUrlsConfig.companyPortalUrlShareLinkName,
        teamsShareLabel: state.settings.clientSettings.shareUrlsConfig.teamsUrlShareLinkName,
        linkPreference: state.settings.notificationSettings.linkPreference,
        portalShareEnabled: state.settings.clientSettings.shareUrlsConfig.toggleCompanyPortalUrl,
        teamsShareEnabled: state.settings.clientSettings.shareUrlsConfig.toggleTeamsUrl,
        sharepointShareEnabled: state.settings.clientSettings.shareUrlsConfig.toggleSpUrl,
        portalUrl: state.settings.clientSettings.teams.companyPortalUrl
    }),
    {
    }
);

type PropsWithRedux = ConnectedProps<typeof connector>;
export default connector(InternalShareScreen);